// ============================================
// AJUSTESVIGILANTE.JS — los umbrales del vigilante, en una pantalla
// ============================================
// Encargo explicito de Ricardo (punto 20): los umbrales son CONFIGURABLES por direccion
// desde una pantalla, NO escritos en el codigo. Si hay que tocar un archivo y desplegar
// para cambiar "7 dias" por "10", en la practica no se cambia nunca.
//
// Los limites (minimo/maximo) y la validacion viven en el SERVIDOR: aqui solo se pintan.
// Guardar exige rol direccion; a cualquier otro el backend le responde 403 y la pantalla
// se queda en modo lectura.

import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from './config/api';
import { authHeaders } from './utils/storage';
import useResponsive from './hooks/useResponsive';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const GRIS = '#5e666e';
const GRIS_CLARO = '#9aa3ab';
const BORDE = '#e6e8ea';

const GRUPOS = {
  general: 'Cómo y cuándo avisa',
  cotizaciones: 'Cotizaciones',
  escalado: 'Escalado a dirección',
  atasco: 'Expedientes atascados',
  listas: 'Listas con precio congelado',
};

export default function AjustesVigilante() {
  const [config, setConfig] = useState([]);
  const [borrador, setBorrador] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [error, setError] = useState(null);
  const { isMobile } = useResponsive();

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/vigilante/config`, { headers: authHeaders() }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'No se pudo leer la configuración');
      setConfig(r.config);
      setBorrador({});
    } catch (e) { setError(e.message); } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (clave, valor) => setBorrador(b => ({ ...b, [clave]: valor }));

  const guardar = async () => {
    setGuardando(true); setError(null); setMensaje(null);
    try {
      const cambios = {};
      for (const [k, v] of Object.entries(borrador)) cambios[k] = Number(v);
      const r = await fetch(`${API_BASE}/api/vigilante/config`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cambios }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'No se pudo guardar');
      setMensaje(`Guardado. ${r.aplicados.length} ajuste(s) actualizado(s).`);
      cargar();
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  const recalcular = async () => {
    setGuardando(true); setError(null); setMensaje(null);
    try {
      const r = await fetch(`${API_BASE}/api/vigilante/barrer`, { method: 'POST', headers: authHeaders() })
        .then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'No se pudo recalcular');
      setMensaje(`Recalculado: ${r.vivos} avisos vivos, ${r.nuevos} nuevos, ${r.cerrados} cerrados, ${r.escalados} escalados.`);
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  const hayCambios = Object.keys(borrador).length > 0;
  const tarjeta = { background: '#fff', border: `1px solid ${BORDE}`, borderRadius: 12, padding: isMobile ? 14 : 18, marginBottom: 14 };

  const porGrupo = {};
  for (const c of config) (porGrupo[c.grupo] = porGrupo[c.grupo] || []).push(c);

  return (
    <div style={{ fontFamily: PP, color: GRIS, padding: isMobile ? 12 : 20 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#2e3338', margin: 0 }}>
        👁️ Ajustes del vigilante
      </h2>
      <p style={{ fontSize: 13, marginTop: 6, marginBottom: 16, maxWidth: 760 }}>
        Cada mañana el vigilante revisa los expedientes, avisa al responsable en su Mi día y,
        si nadie resuelve algo grave, lo sube a dirección. Aquí se decide con qué severidad.
        Súbelos si hay demasiado ruido; bájalos si se está escapando trabajo.
      </p>

      {cargando && <div style={{ ...tarjeta, textAlign: 'center', color: GRIS_CLARO }}>Cargando…</div>}
      {error && <div style={{ ...tarjeta, borderColor: '#e0b4b4', background: '#fdf3f3', color: '#8a4040' }}>{error}</div>}
      {mensaje && <div style={{ ...tarjeta, borderColor: '#b8e8d4', background: '#e8faf5', color: '#1b6b4f' }}>{mensaje}</div>}

      {!cargando && Object.entries(GRUPOS).map(([g, titulo]) => (
        porGrupo[g] && porGrupo[g].length > 0 && (
          <div key={g} style={tarjeta}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#2e3338', marginBottom: 12 }}>{titulo}</div>
            {porGrupo[g].map(c => {
              const esInterruptor = c.minimo === 0 && c.maximo === 1;
              const valor = borrador[c.clave] !== undefined ? borrador[c.clave] : c.valor;
              return (
                <div key={c.clave} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap',
                  padding: '10px 0', borderTop: `1px solid ${BORDE}`,
                }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#2e3338' }}>{c.etiqueta}</div>
                    {c.descripcion && (
                      <div style={{ fontSize: 12, color: GRIS_CLARO, marginTop: 3, wordBreak: 'break-word' }}>
                        {c.descripcion}
                      </div>
                    )}
                    {c.actualizado_por && (
                      <div style={{ fontSize: 11, color: GRIS_CLARO, marginTop: 3 }}>
                        Último cambio: {c.actualizado_por}
                      </div>
                    )}
                  </div>
                  {esInterruptor ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[[1, 'Sí'], [0, 'No']].map(([v, t]) => (
                        <button key={v} onClick={() => cambiar(c.clave, v)}
                          style={{
                            padding: '8px 16px', minHeight: 40, borderRadius: 8, cursor: 'pointer',
                            fontFamily: PP, fontSize: 13, fontWeight: Number(valor) === v ? 700 : 500,
                            background: Number(valor) === v ? AZUL : '#fff',
                            color: Number(valor) === v ? '#fff' : GRIS,
                            border: `1px solid ${Number(valor) === v ? AZUL : BORDE}`,
                          }}>{t}</button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="number" min={c.minimo} max={c.maximo} value={valor}
                      onChange={e => cambiar(c.clave, e.target.value)}
                      style={{
                        width: 96, minHeight: 40, padding: '8px 10px', borderRadius: 8,
                        border: `1px solid ${BORDE}`, fontFamily: PP, fontSize: 14, textAlign: 'center',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )
      ))}

      {!cargando && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', position: 'sticky', bottom: 0, paddingTop: 8 }}>
          <button
            onClick={guardar}
            disabled={!hayCambios || guardando}
            style={{
              padding: '10px 18px', minHeight: 44, borderRadius: 8, fontFamily: PP, fontSize: 14, fontWeight: 700,
              background: hayCambios ? AZUL : '#cfd6dd', color: '#fff', border: 'none',
              cursor: hayCambios && !guardando ? 'pointer' : 'default',
            }}
          >{guardando ? 'Guardando…' : 'Guardar cambios'}</button>
          <button
            onClick={recalcular}
            disabled={guardando}
            style={{
              padding: '10px 18px', minHeight: 44, borderRadius: 8, fontFamily: PP, fontSize: 14, fontWeight: 600,
              background: '#fff', color: AZUL, border: `1px solid ${AZUL}`, cursor: guardando ? 'default' : 'pointer',
            }}
          >Recalcular ahora</button>
        </div>
      )}
    </div>
  );
}
